// Dashboard — News Story Discovery for editors.
//
// Layout: a row of stat cards at the top (counts), a Run pipeline button
// that fires `POST /news/admin/pipeline/run` (fire-and-forget on the API
// side; returns 202 immediately), and a tabbed table view for the four
// queues editors care about: Pending sources, Drafts, Published, Feed
// sources. Drafts → Article Review (Phase F). Pending source rows show
// where they are in the pipeline (pending / grouped / processed). Feed
// Sources shows poll cadence and last-polled timestamp.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/Button";
import { Card, CardSubtle } from "../../components/ui/Card";
import { Input, Label, Select } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import { Spinner } from "../../components/ui/Spinner";
import { useAuth } from "../../auth/AuthContext";
import { api, ApiError } from "../../lib/api";

interface Summary {
  pending_sources: number;
  ready_groups: number;
  drafts: number;
  published: number;
  retracted: number;
  active_feeds: number;
}

interface SourceRow {
  id: string;
  source_url: string;
  source_name: string | null;
  source_published_at: string | null;
  original_title: string;
  processing_status: string;
  event_group_id: string | null;
  created_at: string;
}

interface DraftRow {
  id: string;
  title: string;
  summary: string | null;
  image_url: string | null;
  classification_confidence: number | null;
  drafted_at: string;
  drafted_by: string;
  classification: { sub_thread: string; arc: string | null } | null;
}

interface PublishedRow {
  id: string;
  title: string;
  summary: string | null;
  image_url: string | null;
  published_at: string;
  published_by: string;
}

interface StoryQueueRow {
  id: string;
  source_count: number;
  status: string;
  created_at: string;
  updated_at: string;
  published_article_id: string | null;
  pending_resynthesis: boolean;
  source_peek: Array<{ title: string; source: string | null; url: string }>;
}

interface ArticleConflictPreview {
  article_id: string;
  title: string;
  summary: string;
  image_url: string | null;
  status: string;
}

interface FeedSource {
  id: string;
  name: string;
  feed_url: string;
  feed_type: string;
  region: string | null;
  perspective: string | null;
  poll_interval_minutes: number;
  last_polled_at: string | null;
  last_successful_at: string | null;
  is_active: boolean;
}

type Tab = "queue" | "drafts" | "pending" | "published" | "feeds";

export default function DashboardPage() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [tab, setTab] = useState<Tab>("queue");
  const [running, setRunning] = useState(false);
  const [pipelineNotice, setPipelineNotice] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    const s = await api<Summary>("/editor/dashboard/summary");
    setSummary(s);
  }, []);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  async function runPipeline() {
    setRunning(true);
    setPipelineNotice(null);
    try {
      await api("/news/admin/pipeline/run", { method: "POST" });
      setPipelineNotice("Pipeline started — refresh in 30-60 sec to see new drafts.");
    } catch (e: any) {
      setPipelineNotice(`Failed to start: ${e.message}`);
    } finally {
      setRunning(false);
    }
  }

  return (
    <div>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Dashboard</h1>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {pipelineNotice && (
            <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>
              {pipelineNotice}
            </span>
          )}
          <Button onClick={runPipeline} disabled={running}>
            {running ? "Starting…" : "Run pipeline now"}
          </Button>
          <Button variant="secondary" onClick={loadSummary}>
            Refresh stats
          </Button>
        </div>
      </header>

      {/* Stat cards */}
      <div style={styles.statRow}>
        <StatCard label="Pending sources" value={summary?.pending_sources ?? "—"} />
        <StatCard label="Ready groups" value={summary?.ready_groups ?? "—"} />
        <StatCard label="Drafts" value={summary?.drafts ?? "—"} />
        <StatCard label="Published" value={summary?.published ?? "—"} />
        <StatCard label="Active feeds" value={summary?.active_feeds ?? "—"} />
      </div>

      {/* Tabs */}
      <div style={styles.tabBar}>
        <TabBtn active={tab === "queue"} onClick={() => setTab("queue")}>
          Story queue
          {summary?.ready_groups != null && summary.ready_groups > 0 && (
            <span style={styles.tabBadge}>{summary.ready_groups}</span>
          )}
        </TabBtn>
        <TabBtn active={tab === "drafts"} onClick={() => setTab("drafts")}>
          Drafts
        </TabBtn>
        <TabBtn active={tab === "pending"} onClick={() => setTab("pending")}>
          Pending sources
        </TabBtn>
        <TabBtn active={tab === "published"} onClick={() => setTab("published")}>
          Published
        </TabBtn>
        <TabBtn active={tab === "feeds"} onClick={() => setTab("feeds")}>
          Feed sources
        </TabBtn>
      </div>

      {tab === "queue" && <StoryQueueTable onChange={loadSummary} />}
      {tab === "drafts" && <DraftsTable />}
      {tab === "pending" && <SourcesTable />}
      {tab === "published" && <PublishedTable />}
      {tab === "feeds" && <FeedSourcesTable onChange={loadSummary} />}
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <Card padding={16}>
      <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: "var(--text-primary)", marginTop: 4 }}>
        {value}
      </div>
    </Card>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        background: "transparent",
        border: "none",
        borderBottom: active ? "2px solid var(--accent)" : "2px solid transparent",
        color: active ? "var(--text-primary)" : "var(--text-secondary)",
        padding: "10px 16px",
        fontSize: 14,
        fontWeight: 600,
      }}
    >
      {children}
    </button>
  );
}

function StoryQueueTable({ onChange }: { onChange: () => void | Promise<void> }) {
  const [rows, setRows] = useState<StoryQueueRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, "synth" | "skip" | "merge" | null>>({});
  const [mergeSource, setMergeSource] = useState<StoryQueueRow | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<{ groups: StoryQueueRow[] }>(
        "/editor/dashboard/story-queue",
      );
      setRows(r.groups);
    } catch (e: any) {
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function synthesize(groupId: string) {
    setBusy((b) => ({ ...b, [groupId]: "synth" }));
    try {
      await api(`/news/editor/event-groups/${groupId}/synthesize`, {
        method: "POST",
      });
      // Synthesis can take 30-60s; the API returns when done. The group is
      // now `synthesized` so it falls off this list on reload.
      await load();
      await onChange();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy((b) => ({ ...b, [groupId]: null }));
    }
  }

  async function skip(groupId: string) {
    if (!confirm("Skip this story? It won't be synthesized.")) return;
    setBusy((b) => ({ ...b, [groupId]: "skip" }));
    try {
      await api(`/news/editor/event-groups/${groupId}/skip`, {
        method: "POST",
      });
      await load();
      await onChange();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy((b) => ({ ...b, [groupId]: null }));
    }
  }

  if (error) return <ErrorBox message={error} />;
  if (!rows) return <Loading />;
  if (rows.length === 0) {
    return (
      <EmptyBox message="No stories ready — run the pipeline to ingest fresh sources, then groups appear here once clustered." />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {rows.map((g) => {
        const state = busy[g.id];
        return (
          <Card key={g.id}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 12,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 8, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700 }}>
                    {g.source_count} source{g.source_count === 1 ? "" : "s"}
                  </span>
                  <span style={{ color: "var(--text-muted)", fontSize: 12 }}>
                    grouped {formatRelative(g.updated_at)}
                  </span>
                  {g.pending_resynthesis && (
                    <span
                      title="Source set changed after the article was drafted; consider regenerating"
                      style={{
                        background: "rgba(245, 158, 11, 0.14)",
                        color: "#F59E0B",
                        fontSize: 11,
                        fontWeight: 700,
                        padding: "1px 7px",
                        borderRadius: 999,
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                      }}
                    >
                      Source set changed
                    </span>
                  )}
                </div>
                {g.source_peek.length === 0 ? (
                  <div style={{ color: "var(--text-muted)", fontSize: 13 }}>
                    (no source articles loaded)
                  </div>
                ) : (
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {g.source_peek.map((src, i) => (
                      <li key={i} style={{ fontSize: 13, marginBottom: 4 }}>
                        <a
                          href={src.url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: "var(--text-primary)" }}
                        >
                          {truncate(src.title, 110)}
                        </a>
                        {src.source && (
                          <span style={{ color: "var(--text-muted)", marginLeft: 6 }}>
                            — {src.source}
                          </span>
                        )}
                      </li>
                    ))}
                    {g.source_count > g.source_peek.length && (
                      <li
                        style={{
                          color: "var(--text-muted)",
                          fontSize: 12,
                          listStyle: "none",
                          marginLeft: -8,
                        }}
                      >
                        +{g.source_count - g.source_peek.length} more
                      </li>
                    )}
                  </ul>
                )}
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <Button
                  onClick={() => synthesize(g.id)}
                  disabled={!!state}
                  size="sm"
                >
                  {state === "synth" ? "Synthesizing…" : "Synthesize"}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setMergeSource(g)}
                  disabled={!!state}
                >
                  Merge
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => skip(g.id)}
                  disabled={!!state}
                >
                  {state === "skip" ? "Skipping…" : "Skip"}
                </Button>
              </div>
            </div>
          </Card>
        );
      })}

      {mergeSource && (
        <MergeModal
          source={mergeSource}
          allGroups={rows}
          onClose={() => setMergeSource(null)}
          onMerged={async () => {
            setMergeSource(null);
            await load();
            await onChange();
          }}
        />
      )}
    </div>
  );
}

// ─── Merge picker + article-conflict resolver ─────────────────────────────

function MergeModal({
  source,
  allGroups,
  onClose,
  onMerged,
}: {
  source: StoryQueueRow;
  allGroups: StoryQueueRow[];
  onClose: () => void;
  onMerged: () => void | Promise<void>;
}) {
  const targets = allGroups.filter((g) => g.id !== source.id);
  const [merging, setMerging] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{
    targetId: string;
    target: ArticleConflictPreview | null;
    sourcePreview: ArticleConflictPreview | null;
  } | null>(null);

  async function attemptMerge(targetId: string, keepArticle?: "target" | "source") {
    setMerging(targetId);
    setError(null);
    try {
      await api(`/news/editor/event-groups/${targetId}/merge`, {
        method: "POST",
        body: {
          merge_with_id: source.id,
          ...(keepArticle ? { keep_article: keepArticle } : {}),
        },
      });
      await onMerged();
    } catch (e: any) {
      if (e instanceof ApiError && e.code === "ARTICLE_CONFLICT") {
        setConflict({
          targetId,
          target: e.data?.target ?? null,
          sourcePreview: e.data?.source ?? null,
        });
      } else {
        setError(e?.message ?? "Merge failed");
      }
    } finally {
      setMerging(null);
    }
  }

  return (
    <ModalBackdrop onClose={onClose}>
      <div
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-md)",
          maxWidth: 720,
          width: "100%",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: 20, borderBottom: "1px solid var(--border)" }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            Merge this group into another
          </h2>
          <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 6 }}>
            Pick the group that should survive. This group's {source.source_count}{" "}
            source{source.source_count === 1 ? "" : "s"} will be reparented into it.
          </div>
          <div
            style={{
              marginTop: 12,
              padding: 10,
              background: "rgba(255,255,255,0.04)",
              borderRadius: 6,
              fontSize: 12,
              color: "var(--text-secondary)",
            }}
          >
            {source.source_peek.slice(0, 3).map((s, i) => (
              <div key={i} style={{ marginBottom: 2 }}>
                {truncate(s.title, 110)}
                {s.source && <span style={{ color: "var(--text-muted)" }}> — {s.source}</span>}
              </div>
            ))}
          </div>
        </div>

        <div style={{ overflowY: "auto", flex: 1 }}>
          {targets.length === 0 ? (
            <div style={{ padding: 24, color: "var(--text-muted)", textAlign: "center" }}>
              No other groups available to merge into.
            </div>
          ) : (
            targets.map((t) => (
              <button
                key={t.id}
                onClick={() => attemptMerge(t.id)}
                disabled={!!merging}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "14px 20px",
                  background: "transparent",
                  border: "none",
                  borderBottom: "1px solid var(--border)",
                  color: "var(--text-primary)",
                  cursor: merging ? "not-allowed" : "pointer",
                  opacity: merging ? 0.5 : 1,
                }}
              >
                <div style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 6 }}>
                  <span style={{ fontWeight: 700, fontSize: 13 }}>
                    {t.source_count} sources
                  </span>
                  {t.published_article_id && (
                    <span style={{ fontSize: 11, color: "var(--accent)", fontWeight: 600 }}>
                      · has draft
                    </span>
                  )}
                  <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                    · grouped {formatRelative(t.updated_at)}
                  </span>
                </div>
                {t.source_peek.slice(0, 3).map((s, i) => (
                  <div key={i} style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 2 }}>
                    {truncate(s.title, 110)}
                  </div>
                ))}
              </button>
            ))
          )}
        </div>

        <div style={{ padding: 16, borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {error && <span style={{ color: "var(--danger)", fontSize: 12 }}>{error}</span>}
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>

      {conflict && (
        <ArticleConflictModal
          target={conflict.target}
          sourcePreview={conflict.sourcePreview}
          onCancel={() => setConflict(null)}
          onPick={async (which) => {
            const targetId = conflict.targetId;
            setConflict(null);
            await attemptMerge(targetId, which);
          }}
        />
      )}
    </ModalBackdrop>
  );
}

function ArticleConflictModal({
  target,
  sourcePreview,
  onCancel,
  onPick,
}: {
  target: ArticleConflictPreview | null;
  sourcePreview: ArticleConflictPreview | null;
  onCancel: () => void;
  onPick: (which: "target" | "source") => void;
}) {
  return (
    <ModalBackdrop onClose={onCancel}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-md)",
          maxWidth: 820,
          width: "100%",
          maxHeight: "85vh",
          overflowY: "auto",
          padding: 20,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
          Both groups have an article
        </h2>
        <div style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 6, marginBottom: 16 }}>
          Pick which draft should survive. The other one will be discarded.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <ArticleColumn label="Target" article={target} onPick={() => onPick("target")} />
          <ArticleColumn label="Source (this row)" article={sourcePreview} onPick={() => onPick("source")} />
        </div>
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button
            onClick={onCancel}
            style={{ background: "transparent", border: "none", color: "var(--text-secondary)", cursor: "pointer", fontWeight: 600 }}
          >
            Cancel
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function ArticleColumn({
  label,
  article,
  onPick,
}: {
  label: string;
  article: ArticleConflictPreview | null;
  onPick: () => void;
}) {
  if (!article) {
    return (
      <div style={{ border: "1px solid var(--border)", borderRadius: 6, padding: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--accent)", marginBottom: 6 }}>
          {label}
        </div>
        <div style={{ color: "var(--text-muted)", fontSize: 13 }}>No article</div>
      </div>
    );
  }
  return (
    <div style={{ border: "1px solid var(--border)", borderRadius: 6, padding: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--accent)", marginBottom: 6 }}>
        {label}
      </div>
      {article.image_url && (
        <img
          src={article.image_url}
          alt=""
          style={{ width: "100%", aspectRatio: "16 / 9", objectFit: "cover", borderRadius: 4, marginBottom: 8 }}
        />
      )}
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: "var(--text-primary)" }}>
        {article.title}
      </div>
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 6 }}>{article.status}</div>
      <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 10, lineHeight: 1.5 }}>
        {truncate(article.summary, 240)}
      </div>
      <Button size="sm" onClick={onPick}>
        Keep this one
      </Button>
    </div>
  );
}

function ModalBackdrop({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      {children}
    </div>
  );
}

function DraftsTable() {
  const [rows, setRows] = useState<DraftRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<{ drafts: DraftRow[] }>("/editor/dashboard/drafts")
      .then((r) => setRows(r.drafts))
      .catch((e) => setError(e.message));
  }, []);

  if (error) return <ErrorBox message={error} />;
  if (!rows) return <Loading />;
  if (rows.length === 0) return <EmptyBox message="No drafts yet — run the pipeline." />;

  return (
    <div style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Title</th>
            <th style={styles.th}>Classification</th>
            <th style={styles.th}>Drafted</th>
            <th style={styles.th}>By</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={styles.tr}>
              <td style={styles.td}>
                <Link to={`/article/${r.id}`} style={styles.titleLink}>
                  {r.title}
                </Link>
                {r.summary && (
                  <div style={styles.subline}>{truncate(r.summary, 120)}</div>
                )}
              </td>
              <td style={styles.td}>
                {r.classification ? (
                  <>
                    <div>{r.classification.sub_thread}</div>
                    {r.classification.arc && (
                      <div style={{ color: "var(--text-muted)", fontSize: 12 }}>
                        {r.classification.arc}
                      </div>
                    )}
                  </>
                ) : (
                  <span style={{ color: "var(--text-muted)" }}>—</span>
                )}
              </td>
              <td style={styles.td}>{formatRelative(r.drafted_at)}</td>
              <td style={styles.td}>{r.drafted_by}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SourcesTable() {
  const [rows, setRows] = useState<SourceRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<{ sources: SourceRow[] }>("/editor/dashboard/sources?status=pending")
      .then((r) => setRows(r.sources))
      .catch((e) => setError(e.message));
  }, []);

  if (error) return <ErrorBox message={error} />;
  if (!rows) return <Loading />;
  if (rows.length === 0) return <EmptyBox message="No pending source articles." />;

  return (
    <div style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Title</th>
            <th style={styles.th}>Source</th>
            <th style={styles.th}>Status</th>
            <th style={styles.th}>Ingested</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={styles.tr}>
              <td style={styles.td}>
                <a href={r.source_url} target="_blank" rel="noreferrer" style={styles.titleLink}>
                  {r.original_title}
                </a>
              </td>
              <td style={styles.td}>{r.source_name ?? "—"}</td>
              <td style={styles.td}>
                <StatusPill status={r.processing_status} />
              </td>
              <td style={styles.td}>{formatRelative(r.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PublishedTable() {
  const [rows, setRows] = useState<PublishedRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<{ published: PublishedRow[] }>("/editor/dashboard/published")
      .then((r) => setRows(r.published))
      .catch((e) => setError(e.message));
  }, []);

  if (error) return <ErrorBox message={error} />;
  if (!rows) return <Loading />;
  if (rows.length === 0) return <EmptyBox message="Nothing published yet." />;

  return (
    <div style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Title</th>
            <th style={styles.th}>Published</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={styles.tr}>
              <td style={styles.td}>
                <Link to={`/article/${r.id}`} style={styles.titleLink}>
                  {r.title}
                </Link>
                {r.summary && <div style={styles.subline}>{truncate(r.summary, 140)}</div>}
              </td>
              <td style={styles.td}>{formatRelative(r.published_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Everything the create/edit form edits. is_active is edit-only — the create
 *  endpoint does not accept it and the column defaults to true. */
interface FeedDraft {
  name: string;
  feed_url: string;
  feed_type: string;
  region: string;
  perspective: string;
  poll_interval_minutes: string;
  is_active: boolean;
}

const EMPTY_DRAFT: FeedDraft = {
  name: "",
  feed_url: "",
  feed_type: "rss",
  region: "",
  perspective: "",
  poll_interval_minutes: "30",
  is_active: true,
};

/**
 * The API hands zod's flatten().fieldErrors straight through as `error`, and
 * lib/api passes that object in as the Error MESSAGE — so rendering err.message
 * for a 400 prints "[object Object]". Read the structured body instead, and
 * name the field that was wrong: "feed url: Invalid url" is actionable, a
 * generic failure notice is not.
 */
function describeApiError(err: any): string {
  const detail = err?.data?.error ?? err?.message;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    const parts = Object.entries(detail).map(
      ([field, msgs]) =>
        `${field.replace(/_/g, " ")}: ${
          Array.isArray(msgs) ? msgs.join(", ") : String(msgs)
        }`,
    );
    if (parts.length) return parts.join(" · ");
  }
  return "Something went wrong";
}

function FeedSourcesTable({ onChange }: { onChange?: () => void }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [rows, setRows] = useState<FeedSource[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<FeedSource | "new" | null>(null);
  const [deleting, setDeleting] = useState<FeedSource | null>(null);
  const [busyRow, setBusyRow] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<{ feed_sources: FeedSource[] }>(
        "/editor/dashboard/feed-sources",
      );
      setRows(r.feed_sources);
      setError(null);
    } catch (e: any) {
      setError(describeApiError(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function afterWrite() {
    setEditing(null);
    setDeleting(null);
    await load();
    onChange?.();
  }

  // Pausing a feed is the reversible half of deleting it, so it gets to be one
  // click while deletion has to be confirmed.
  async function toggleActive(row: FeedSource) {
    setBusyRow(row.id);
    try {
      await api(`/news/admin/feed-sources/${row.id}`, {
        method: "PUT",
        body: { is_active: !row.is_active },
      });
      await load();
      onChange?.();
    } catch (e: any) {
      setError(describeApiError(e));
    } finally {
      setBusyRow(null);
    }
  }

  if (error && !rows) return <ErrorBox message={error} />;
  if (!rows) return <Loading />;

  // Existing regions offered as suggestions, so a new feed lands in the same
  // bucket as its neighbours rather than starting "West Africa" beside the
  // "West Afrika" already in use.
  const regions = Array.from(
    new Set(rows.map((r) => r.region).filter(Boolean) as string[]),
  ).sort();

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 10,
        }}
      >
        <CardSubtle style={{ margin: 0 }}>
          {isAdmin
            ? "Polling stops the moment a feed is paused or removed. Articles already ingested are unaffected."
            : "Read-only — changing feed sources needs the admin role."}
        </CardSubtle>
        {isAdmin && (
          <Button onClick={() => setEditing("new")}>Add feed source</Button>
        )}
      </div>

      {error && (
        <CardSubtle style={{ color: "var(--danger)", marginBottom: 8 }}>
          {error}
        </CardSubtle>
      )}

      <datalist id="feed-regions">
        {regions.map((r) => (
          <option key={r} value={r} />
        ))}
      </datalist>

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>URL</th>
              <th style={styles.th}>Region</th>
              <th style={styles.th}>Poll</th>
              <th style={styles.th}>Last polled</th>
              <th style={styles.th}>Active</th>
              {isAdmin && <th style={styles.th} />}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id}
                style={{ ...styles.tr, opacity: r.is_active ? 1 : 0.55 }}
              >
                <td style={styles.td}>
                  <strong>{r.name}</strong>
                  {r.perspective && (
                    <div style={{ color: "var(--text-muted)", fontSize: 12 }}>
                      {r.perspective}
                    </div>
                  )}
                </td>
                <td
                  style={{
                    ...styles.td,
                    fontSize: 12,
                    color: "var(--text-secondary)",
                  }}
                >
                  <a
                    href={r.feed_url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "var(--text-secondary)" }}
                  >
                    {truncate(r.feed_url, 60)}
                  </a>
                </td>
                <td style={styles.td}>{r.region ?? "—"}</td>
                <td style={styles.td}>{r.poll_interval_minutes}m</td>
                <td style={styles.td}>{formatRelative(r.last_polled_at)}</td>
                <td style={styles.td}>
                  {isAdmin ? (
                    <button
                      type="button"
                      onClick={() => toggleActive(r)}
                      disabled={busyRow === r.id}
                      title={
                        r.is_active
                          ? "Stop polling this feed"
                          : "Start polling this feed again"
                      }
                      style={{
                        ...styles.rowState,
                        color: r.is_active
                          ? "var(--success)"
                          : "var(--text-muted)",
                      }}
                    >
                      {r.is_active ? "active" : "paused"}
                    </button>
                  ) : (
                    <span
                      style={{
                        color: r.is_active
                          ? "var(--success)"
                          : "var(--text-muted)",
                      }}
                    >
                      {r.is_active ? "active" : "paused"}
                    </span>
                  )}
                </td>
                {isAdmin && (
                  <td style={{ ...styles.td, whiteSpace: "nowrap" }}>
                    <button
                      type="button"
                      onClick={() => setEditing(r)}
                      style={styles.rowLink}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleting(r)}
                      style={{
                        ...styles.rowLink,
                        marginLeft: 10,
                        color: "var(--danger)",
                      }}
                    >
                      Delete
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <FeedSourceModal
          source={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={afterWrite}
        />
      )}

      {deleting && (
        <Modal open onClose={() => setDeleting(null)} title="Remove feed source" width={520}>
          <p style={{ marginTop: 0 }}>
            Stop polling <strong>{deleting.name}</strong> and delete its
            configuration?
          </p>
          <CardSubtle>
            Articles already ingested from it stay where they are — this only
            removes the feed. If you just want it to stop for now,{" "}
            <strong>pause</strong> it instead: that is reversible and this is
            not.
          </CardSubtle>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <Button
              variant="secondary"
              onClick={async () => {
                try {
                  await api(`/news/admin/feed-sources/${deleting.id}`, {
                    method: "DELETE",
                  });
                  await afterWrite();
                } catch (e: any) {
                  setError(describeApiError(e));
                  setDeleting(null);
                }
              }}
            >
              Delete permanently
            </Button>
            <Button onClick={() => setDeleting(null)}>Keep it</Button>
          </div>
        </Modal>
      )}
    </>
  );
}

function FeedSourceModal({
  source,
  onClose,
  onSaved,
}: {
  source: FeedSource | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [d, setD] = useState<FeedDraft>(
    source
      ? {
          name: source.name,
          feed_url: source.feed_url,
          feed_type: source.feed_type,
          region: source.region ?? "",
          perspective: source.perspective ?? "",
          poll_interval_minutes: String(source.poll_interval_minutes),
          is_active: source.is_active,
        }
      : EMPTY_DRAFT,
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const set = <K extends keyof FeedDraft>(k: K, v: FeedDraft[K]) =>
    setD((p) => ({ ...p, [k]: v }));

  const poll = Number(d.poll_interval_minutes);
  const pollValid = Number.isInteger(poll) && poll >= 5;
  const canSave =
    d.name.trim().length > 0 && d.feed_url.trim().length > 0 && pollValid;

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      // Optional text fields go as null rather than "" on edit — the column is
      // nullable and an empty string would read as a region actually named "".
      const common = {
        name: d.name.trim(),
        feed_url: d.feed_url.trim(),
        feed_type: d.feed_type,
        region: d.region.trim() || null,
        perspective: d.perspective.trim() || null,
        poll_interval_minutes: poll,
      };

      if (source) {
        await api(`/news/admin/feed-sources/${source.id}`, {
          method: "PUT",
          body: { ...common, is_active: d.is_active },
        });
      } else {
        // The create endpoint takes neither is_active nor nulls for the
        // optional fields, so they are omitted rather than sent empty.
        const body: Record<string, unknown> = {
          name: common.name,
          feed_url: common.feed_url,
          feed_type: common.feed_type,
          poll_interval_minutes: common.poll_interval_minutes,
        };
        if (common.region) body.region = common.region;
        if (common.perspective) body.perspective = common.perspective;
        await api("/news/admin/feed-sources", { method: "POST", body });
      }
      onSaved();
    } catch (e: any) {
      setErr(describeApiError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={source ? "Edit feed source" : "Add feed source"}
      width={580}
    >
      <Label>Name</Label>
      <Input
        value={d.name}
        onChange={(e) => set("name", e.target.value)}
        placeholder="Google News — Ethiopia and the GERD"
        autoFocus
      />

      <Label style={{ marginTop: 12 }}>Feed URL</Label>
      <Input
        value={d.feed_url}
        onChange={(e) => set("feed_url", e.target.value)}
        placeholder="https://news.google.com/rss/search?q=…"
      />

      <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
        <div style={{ flex: 1 }}>
          <Label>Region</Label>
          <Input
            list="feed-regions"
            value={d.region}
            onChange={(e) => set("region", e.target.value)}
            placeholder="East Afrika"
          />
        </div>
        <div style={{ width: 130 }}>
          <Label>Poll every</Label>
          <Input
            type="number"
            min={5}
            value={d.poll_interval_minutes}
            onChange={(e) => set("poll_interval_minutes", e.target.value)}
          />
        </div>
        <div style={{ width: 110 }}>
          <Label>Type</Label>
          <Select
            value={d.feed_type}
            onChange={(e) => set("feed_type", e.target.value)}
          >
            <option value="rss">RSS</option>
            <option value="api">API</option>
          </Select>
        </div>
      </div>
      {!pollValid && d.poll_interval_minutes !== "" && (
        <CardSubtle style={{ color: "var(--danger)", marginTop: 4 }}>
          Minutes must be a whole number, 5 or more.
        </CardSubtle>
      )}

      <Label style={{ marginTop: 12 }}>Perspective</Label>
      <Input
        value={d.perspective}
        onChange={(e) => set("perspective", e.target.value)}
        placeholder="State media · Chinese framing"
      />
      <CardSubtle style={{ marginTop: 4 }}>
        Optional note on where this outlet stands. Shown under the name so an
        editor reading a cluster knows who they are reading.
      </CardSubtle>

      {source && (
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 14,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={d.is_active}
            onChange={(e) => set("is_active", e.target.checked)}
          />
          <span>Active — poll this feed</span>
        </label>
      )}

      {err && (
        <CardSubtle style={{ color: "var(--danger)", marginTop: 10 }}>
          {err}
        </CardSubtle>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <Button onClick={save} disabled={!canSave || saving}>
          {saving ? "Saving…" : source ? "Save changes" : "Add feed source"}
        </Button>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}

function StatusPill({ status }: { status: string }) {
  const colorByStatus: Record<string, string> = {
    pending: "var(--text-muted)",
    grouped: "var(--accent)",
    processed: "var(--success)",
    skipped: "var(--text-muted)",
  };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.4,
        background: "rgba(255,255,255,0.06)",
        color: colorByStatus[status] ?? "var(--text-secondary)",
      }}
    >
      {status}
    </span>
  );
}

function Loading() {
  return (
    <div style={{ padding: 24, textAlign: "center" }}>
      <Spinner size={20} />
    </div>
  );
}

function EmptyBox({ message }: { message: string }) {
  return (
    <Card>
      <CardSubtle>{message}</CardSubtle>
    </Card>
  );
}

function ErrorBox({ message }: { message: string }) {
  return (
    <Card>
      <CardSubtle style={{ color: "var(--danger)" }}>{message}</CardSubtle>
    </Card>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function formatRelative(ts: string | null | undefined): string {
  if (!ts) return "—";
  const d = new Date(ts);
  const diff = Date.now() - d.getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return d.toLocaleDateString();
}

const styles: Record<string, React.CSSProperties> = {
  // The active/paused toggle: coloured by state, no underline — the word
  // carries the meaning and the underline only added noise.
  rowState: {
    background: "none",
    border: "none",
    padding: 0,
    font: "inherit",
    fontSize: 13,
    cursor: "pointer",
  },
  rowLink: {
    background: "none",
    border: "none",
    padding: 0,
    font: "inherit",
    fontSize: 13,
    color: "var(--accent)",
    cursor: "pointer",
    textDecoration: "underline",
    textUnderlineOffset: 2,
  },
  statRow: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: 12,
    marginBottom: 24,
  },
  tabBar: {
    display: "flex",
    borderBottom: "1px solid var(--border)",
    marginBottom: 16,
  },
  tabBadge: {
    display: "inline-block",
    marginLeft: 6,
    padding: "1px 7px",
    background: "var(--accent)",
    color: "#0e0e0f",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700,
    lineHeight: "16px",
    verticalAlign: "middle",
  },
  tableWrap: {
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-md)",
    overflow: "hidden",
    background: "var(--surface)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: "12px 16px",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "var(--text-muted)",
    borderBottom: "1px solid var(--border)",
    background: "rgba(255,255,255,0.02)",
  },
  tr: {
    borderBottom: "1px solid var(--border)",
  },
  td: {
    padding: "12px 16px",
    fontSize: 14,
    color: "var(--text-primary)",
    verticalAlign: "top",
  },
  titleLink: {
    color: "var(--text-primary)",
    textDecoration: "none",
    fontWeight: 600,
  },
  subline: {
    fontSize: 12,
    color: "var(--text-muted)",
    marginTop: 4,
  },
};
